import { useEffect, useRef, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/utils/merge';
import { InterestContext } from './interest';

// Shared building blocks of the public website.

export const Container = ({ className, children }) => (
	<div className={cn('mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8', className)}>
		{children}
	</div>
);

export const Eyebrow = ({ children, tone = 'blue' }) => (
	<span
		className={cn(
			'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase',
			tone === 'blue' && 'bg-(--dm-blue-soft) text-(--dm-blue)',
			tone === 'orange' && 'bg-(--dm-orange-soft) text-(--dm-orange-hover)',
			tone === 'light' && 'bg-white/10 text-white',
		)}
	>
		{children}
	</span>
);

export const SectionHeading = ({ eyebrow, title, text, id, align = 'center', className }) => (
	<div
		className={cn(
			'mb-10 flex max-w-2xl flex-col gap-4 sm:mb-14',
			align === 'center' ? 'mx-auto items-center text-center' : 'items-start text-left',
			className,
		)}
	>
		{eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
		<h2 id={id} className='text-3xl leading-tight font-extrabold sm:text-4xl lg:text-[44px]'>
			{title}
		</h2>
		{text && <p className='text-base leading-relaxed text-(--dm-muted) sm:text-lg'>{text}</p>}
	</div>
);

const buttonBase =
	'inline-flex min-h-12 items-center justify-center gap-2 rounded-xl px-6 text-[15px] font-semibold transition-all duration-200 select-none active:scale-[0.98]';

const buttonVariants = {
	primary:
		'bg-(--dm-orange) text-white shadow-[0_8px_20px_rgba(255,107,44,0.28)] hover:bg-(--dm-orange-hover) hover:shadow-[0_10px_24px_rgba(255,107,44,0.36)]',
	secondary:
		'border border-(--dm-line) bg-white text-(--dm-navy) shadow-sm hover:border-(--dm-blue) hover:text-(--dm-blue)',
	navy: 'bg-(--dm-navy) text-white hover:bg-(--dm-navy-deep)',
	ghostLight:
		'border border-white/25 bg-white/5 text-white hover:border-white/60 hover:bg-white/10',
	link: 'min-h-0 px-0 text-(--dm-blue) hover:text-(--dm-navy)',
};

// A link (href) or a button (onClick) with the site's button look.
export const Action = ({ href, onClick, variant = 'primary', className, children, ...rest }) => {
	const cls = cn(buttonBase, buttonVariants[variant], className);
	if (href) {
		return (
			<a href={href} onClick={onClick} className={cls} {...rest}>
				{children}
			</a>
		);
	}
	return (
		<button type='button' onClick={onClick} className={cls} {...rest}>
			{children}
		</button>
	);
};

// Fades its content in the first time it scrolls into view.
export const Reveal = ({ as: Tag = 'div', delay = 0, className, children, ...rest }) => {
	const ref = useRef(null);

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof IntersectionObserver === 'undefined') return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
		// Already on screen at load: show at once, no flash.
		if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;
		el.classList.add('dm-reveal-pending');
		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					el.classList.remove('dm-reveal-pending');
					obs.disconnect();
				}
			},
			{ rootMargin: '0px 0px -8% 0px' },
		);
		obs.observe(el);
		return () => {
			obs.disconnect();
			el.classList.remove('dm-reveal-pending');
		};
	}, []);

	return (
		<Tag
			ref={ref}
			className={cn('dm-reveal', className)}
			style={delay ? { transitionDelay: `${delay}ms` } : undefined}
			{...rest}
		>
			{children}
		</Tag>
	);
};

// Stand-in for a real screenshot until one is set in content.js.
const ShotPlaceholder = ({ label, compact }) => (
	<div
		className='flex h-full w-full flex-col items-center justify-center gap-2 bg-[linear-gradient(135deg,#f1f5fb_0%,#e6eef9_100%)] p-4 text-center'
		role='img'
		aria-label={label}
	>
		<ImageIcon className='text-(--dm-blue)/50' size={compact ? 22 : 30} aria-hidden='true' />
		<span className={cn('font-medium text-(--dm-muted)', compact ? 'text-[11px]' : 'text-sm')}>
			{label}
		</span>
	</div>
);

const Shot = ({ src, alt, label, eager, compact }) =>
	src ? (
		<img
			src={src}
			alt={alt}
			className='h-full w-full object-cover object-top'
			loading={eager ? 'eager' : 'lazy'}
			fetchPriority={eager ? 'high' : 'auto'}
			decoding='async'
		/>
	) : (
		<ShotPlaceholder label={label} compact={compact} />
	);

export const BrowserFrame = ({ src, alt, label, url = 'deliveryman.gr', eager, className }) => (
	<div
		className={cn(
			'overflow-hidden rounded-2xl border border-(--dm-line) bg-white shadow-(--dm-shadow-lg)',
			className,
		)}
	>
		<div className='flex items-center gap-1.5 border-b border-(--dm-line) bg-[#f3f6fa] px-4 py-2.5'>
			<span className='h-2.5 w-2.5 rounded-full bg-[#ff5f57]' />
			<span className='h-2.5 w-2.5 rounded-full bg-[#febc2e]' />
			<span className='h-2.5 w-2.5 rounded-full bg-[#28c840]' />
			<span className='mx-auto rounded-md bg-white px-10 py-0.5 text-[11px] text-(--dm-muted)'>
				{url}
			</span>
		</div>
		<div className='aspect-[16/10]'>
			<Shot src={src} alt={alt} label={label} eager={eager} />
		</div>
	</div>
);

export const PhoneFrame = ({ src, alt, label, eager, className }) => (
	<div
		className={cn(
			'rounded-[2rem] border-[6px] border-[#0d1b2a] bg-[#0d1b2a] shadow-(--dm-shadow-lg)',
			className,
		)}
	>
		<div className='relative aspect-[9/19] overflow-hidden rounded-[1.6rem] bg-white'>
			<span className='absolute top-1.5 left-1/2 z-10 h-4 w-16 -translate-x-1/2 rounded-full bg-[#0d1b2a]' />
			<Shot src={src} alt={alt} label={label} eager={eager} compact />
		</div>
	</div>
);

// Lets any button on the page jump to the interest form with a role chosen.
export const InterestProvider = ({ children }) => {
	const [role, setRole] = useState('shop');
	const openInterest = next => {
		if (next) setRole(next);
		const el = document.getElementById('endiaferon');
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			// Move focus for keyboard and screen-reader users, without a second scroll.
			setTimeout(() => el.querySelector('select, input')?.focus({ preventScroll: true }), 450);
		}
	};
	return (
		<InterestContext.Provider value={{ role, setRole, openInterest }}>
			{children}
		</InterestContext.Provider>
	);
};
